import React, { Fragment } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  tableCellClasses,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import { FontFamily } from "assets";
import Colors from "assets/Style/Colors";
import { CommaSeparator } from "utils";
import { CheckIcon, PendingIcon } from "assets";
import ShippingLanscape from "./shipping_short_landscape.svg";
import styled from "@emotion/styled";

const Row = styled(TableRow)(({ theme }) => ({
  border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: "10px",
    padding: "0px !important",
    height: "70px", // make it consistent with header height
    background: Colors.primary,
    border: 0,
    color: Colors.white,
    verticalAlign: "middle",
    textAlign: "center",
  },

  [`&.${tableCellClasses.body}`]: {
    fontSize: '8px',
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    padding: "2px !important",
    whiteSpace: "normal",
    wordBreak: "break-word",

    ".MuiBox-root": {
      display: "flex",
      gap: "2px",
      alignItems: "center",
      justifyContent: "center",
      ".MuiBox-root": {
        cursor: "pointer",
      },
    },
    svg: {
      width: "auto",
      height: "24px",
    },
    ".MuiTypography-root": {
      textTransform: "capitalize",
      fontFamily: FontFamily.NunitoRegular,
      whiteSpace: "nowrap",
    },
    ".MuiButtonBase-root": {
      width: "28px",
      height: "28px",
    },
  },
}));

const ShippingSoaPdf = ({
  mode,
  currencyExchangeRate,
  tableHead,
  selectedCustomer,
  fromdate,
  todate,
  resultData,
  total,
  selectedStatus,
  shippingVault,
  totalVCC,
  vehicleVault,
  mainTotal,
  totalBalance,
  totalPaid,
  allSums,
  netDueTotal,
  shippingReceivableCV,
  totalShippingWallet,
  totalVehicleWallet,
  visibleColumns,
  dueBreakup,
  subTotal,
  copied,
  copyContent,
  status,
  totalCustomWallet,
  totalParkingWallet,
}) => {
  const currentDate = moment(new Date()).format("DD-MMM-YY HH:mm");
  const isShortHead = (tableHead?.length ?? 0) < 28;
  const MAKE_MODEL_COL_INDEX = 2;
  const BASE_COL_WIDTH = 100;
  const MAKE_MODEL_COL_WIDTH = 200;

  const formatAccounting = (val) => {
    const num = Number(val) || 0;
    const pretty = CommaSeparator(Math.abs(num).toFixed(2));
    return num < 0 ? `(${pretty})` : pretty;
  };

  const renderCellContent = (item, colIndex, index) => {
    const date = moment(item?.vehicle?.vcc?.vcc_expiry_date).format(
      "DD-MM-YYYY"
    );

    const dis = colIndex === MAKE_MODEL_COL_INDEX ? "yes" : "no";
    const targetDate = moment(date, "DD-MM-YYYY");
    let subtotalAmount = parseFloat(item?.invoice?.total) + parseFloat(item?.discount);
    let daysRemaining = targetDate > moment();
    // console.log(daysRemaining);
    switch (colIndex) {
      case 0:
        return index + 1;
      // return item?.booking?.purchase_date
      //   ? moment(item?.booking?.purchase_date).format("DD-MMM-YYYY")
      //   : "-";
      case 1:
        return item?.booking?.purchase_date
          ? moment(item?.booking?.purchase_date).format("DD-MMM-YYYY")
          : "-";
      case 2:
        const make = item?.booking?.veh_make?.name ?? "";
        const model = item?.booking?.veh_model?.name ?? "";
        const color = item?.booking?.color ?? "";
        const MakeModelColor = `${model} - ${make} -${color}`;
        return (
          <Box>
            <Box
              component={"div"}
              className="pdf-show"
              sx={{ display: "none !important" }}
            >
              {MakeModelColor ?? "-"}
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={copied ? "copied" : item?.booking?.lot_number ?? "-"}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -2],
                      },
                    },
                  ],
                },
              }}
              onClick={() => copyContent(item?.booking?.lot_number)}
            >
              {item?.booking?.lot_number?.length > 12
                ? item?.booking?.lot_number?.slice(0, 12) + "..."
                : item?.booking?.lot_number}
            </Tooltip>
            <Box
              component={"div"}
              className="pdf-show"
              sx={{ display: "none !important" }}
            >
              {item?.booking?.lot_number ?? "-"}
            </Box>
          </Box>
        );
      case 4:
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={copied ? "copied" : item?.booking?.vin ?? "-"}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -2],
                      },
                    },
                  ],
                },
              }}
              onClick={() => copyContent(item?.booking?.vin)}
            >
              {item?.booking?.vin?.length > 12
                ? item?.booking?.vin?.slice(0, 12) + "..."
                : item?.booking?.vin}
            </Tooltip>
            <Box
              component={"div"}
              className="pdf-show"
              sx={{ display: "none !important" }}
            >
              {item?.booking?.vin ?? "-"}
            </Box>
          </Box>
        );
      case 5:
        return item?.vehicle?.arrived_port_date
          ? moment(item?.vehicle?.arrived_port_date).format("DD-MMM-YYYY")
          : "-";
      case 6:
        return (
          <>
            {CommaSeparator(parseFloat(item?.shipping_charges).toFixed(2)) ??
              "-"}
          </>
        );
      case 7:
        return (
          <>
            {parseFloat(item?.late_fee) > 0
              ? CommaSeparator(parseFloat(item?.late_fee).toFixed(2))
              : "-"}
          </>
        );
      case 8:
        return (
          <>
            {parseFloat(item?.storage) > 0
              ? CommaSeparator(parseFloat(item?.storage).toFixed(2))
              : "-"}
          </>
        );
      case 9:
        return (
          <>
            {parseFloat(item?.category_a) > 0
              ? CommaSeparator(parseFloat(item?.category_a).toFixed(2))
              : "-"}
          </>
        );
      case 10:
        return (
          <>

            {parseFloat(item?.broker_fee) > 0
              ? CommaSeparator(parseFloat(item?.broker_fee).toFixed(2))
              : "-"}
          </>
        );
      case 11:
        return (
          <>
            {parseFloat(item?.title_fee) > 0
              ? CommaSeparator(parseFloat(item?.title_fee).toFixed(2))
              : "-"}
          </>
        );
      case 12: {
        const cutoff = moment("25-05-2025", "DD-MM-YYYY");

        const purchaseMoment = item?.createdAt
          ? moment(item?.createdAt)
          : null;

        const useV1 = purchaseMoment ? purchaseMoment.isSameOrBefore(cutoff, "day") : false;

        const value = useV1 ? item?.other_charge : item?.clearance_att_fee;

        return (
          <>
            {parseFloat(value) > 0
              ? CommaSeparator(parseFloat(value).toFixed(2))
              : "-"}
          </>
        );
      }
      // case 12:
      //   return (
      //     <>
      //       {parseFloat(item?.clearance_att_fee) > 0
      //         ? CommaSeparator(parseFloat(item?.clearance_att_fee).toFixed(2))
      //         : "-"}
      //     </>
      //   );
      case 13:
        return (
          <>
            {parseFloat(item?.current_suv) > 0
              ? CommaSeparator(parseFloat(item?.current_suv).toFixed(2))
              : "-"}
          </>
        );

      case 14:
        return <>{parseFloat(item?.hybrid_fee) > 0 ? item?.hybrid_fee : "-"}</>;

      case 15:
        return <>{parseFloat(item?.heavy_equipment_fee) > 0 ? item?.heavy_equipment_fee : "-"}</>;

      case 16:
        return <>{parseFloat(item?.parts_pickup_fee) > 0 ? item?.parts_pickup_fee : "-"}</>;

      case 17:
        return <>{parseFloat(item?.wrapping_fee) > 0 ? item?.wrapping_fee : "-"}</>;

      case 18:
        return <>{parseFloat(item?.relist_reversal_fee) > 0 ? item?.relist_reversal_fee : "-"}</>;

      case 19:
        return <>{parseFloat(item?.misc_fee) > 0 ? item?.misc_fee : "-"}</>;

      case 20:
        return (
          <>

            {parseFloat(item?.invoice?.custom_total) > 0
              ? CommaSeparator(parseFloat(item?.invoice?.custom_total).toFixed(2))
              : "-"}
            {/* $
                  {
                    CommaSeparator(
                      parseFloat(
                        (parseFloat(item?.shipping_charges || 0) +
                          parseFloat(item?.late_fee || 0) +
                          parseFloat(item?.storage || 0) +
                          parseFloat(item?.category_a || 0) +
                          parseFloat(item?.broker_fee || 0) +
                          parseFloat(item?.title_fee || 0) +
                          parseFloat(item?.other_charge || 0) +
                          parseFloat(item?.vehicle?.gate_pass?.details[0]?.parking_due || 0)
                        )).toFixed(2)
                    ) ?? "-"
                  } */}
          </>
        );
      case 21:
        return (
          <>
            {parseFloat(item?.invoice?.vat_total) > 0
              ? CommaSeparator(parseFloat(item?.invoice?.vat_total).toFixed(2))
              : "-"}
          </>
        );
      case 22:
        return (
          <>
            {CommaSeparator(parseFloat(subtotalAmount).toFixed(2)) ?? "-"}

            {/* {CommaSeparator(
              parseFloat(
                parseFloat(item?.shipping_charges || 0) +
                parseFloat(item?.late_fee || 0) +
                parseFloat(item?.storage || 0) +
                parseFloat(item?.category_a || 0) +
                parseFloat(item?.broker_fee || 0) +
                parseFloat(item?.title_fee || 0) +
                parseFloat(item?.other_charge || 0)
              ).toFixed(2)
            ) ?? "-"} */}
          </>
        );
      case 23:
        return (
          <>{CommaSeparator(parseFloat(item?.discount).toFixed(2)) ?? "-"}</>
        );
      case 24:
        return (
          <>
            {" "}
            {CommaSeparator(
              parseFloat(
                parseFloat(subtotalAmount).toFixed(2) - parseFloat(item?.discount).toFixed(2)
              ).toFixed(2)
            )}
          </>
        );
      case 25:
        return (
          <b>{CommaSeparator(parseFloat(item?.invoice?.paid).toFixed(2))}</b>

          // <Box
          //   component={"div"}
          //   className="pdf-hide"
          //   onClick={
          //     item?.invoice
          //       ? () =>
          //         navigate(`/client-invoice-detail/${item?.invoice?.id}`, {
          //           state: {
          //             shipping: true,
          //           },
          //         })
          //       : () => {
          //         ErrorToaster("Invoice Not Available");
          //       }
          //   }
          // >
          //   <IconButton
          //     sx={{
          //       bgcolor: Colors.primary,
          //       "&:hover": {
          //         bgcolor: Colors.primary,
          //       },
          //     }}
          //   >
          //     <EyeIcon />
          //   </IconButton>
          //   <Typography variant="body2">View</Typography>
          // </Box>
        );
      case 26:
        return (
          <b>{CommaSeparator(parseFloat(item?.invoice?.balance).toFixed(2))}</b>
        );

      case 27:
        return (
          <b>
            {moment().diff(item?.vehicle?.arrived_port_date, "days") || 0} Days
          </b>
        );

      case 28:
        return (
          <Box
            sx={{
              path: {
                fill:
                  item?.invoice?.balance && item?.invoice?.balance === "0.000"
                    ? Colors.primary
                    : item?.invoice?.balance !== "0.000"
                      ? Colors.danger
                      : Colors.bluishCyan,
              },
            }}
          >
            <span className="pdf-hide">
              {" "}
              {item?.vehicle?.arrived_port_date === null &&
                item?.vehicle?.loading_date ? (
                // On The Way status icon
                <PendingIcon />
              ) : (
                (() => {
                  const arrivedDate = moment(
                    item?.vehicle?.arrived_port_date
                  );
                  const dueThresholdDate = moment(arrivedDate).add(15, "days");
                  const today = moment();

                  if (
                    today.isSameOrAfter(dueThresholdDate, "day") &&
                    item?.invoice?.balance !== "0.000"
                  ) {
                    // Arrived And Due
                    return <PendingIcon />;
                  }

                  if (item?.invoice?.balance === "0.000") {
                    return <CheckIcon />;
                  }

                  if (item?.invoice?.balance !== "0.000") {
                    return <PendingIcon />;
                  }

                  return <CheckIcon />;
                })()
              )}
            </span>
            <Typography sx={{ fontSize: '8px !important' }}>
              {item?.vehicle?.arrived_port_date === null &&
                item?.vehicle?.loading_date
                ? "On The Way"
                : (() => {
                  const arrivedDate = moment(
                    item?.vehicle?.arrived_port_date
                  );
                  const dueThresholdDate = moment(arrivedDate).add(
                    15,
                    "days"
                  );
                  const today = moment();
                  if (
                    today.isSameOrAfter(dueThresholdDate, "day") &&
                    item?.invoice?.balance !== "0.000"
                  ) {
                    return "Arrived & Due";
                  }

                  if (item?.invoice?.balance === "0.000") {
                    return "paid";
                  }
                  if (item?.invoice?.balance !== "0.000") {
                    return "Arrived And Due";
                  }

                  return "Partial";
                })()}
            </Typography>
          </Box>
        );

      default:
        return "-";
    }
  };

  return (
    <Box
      sx={{
        minHeight: mode === "landscape" ? "210mm" : "297mm",
        overflowX: "auto",
        margin: "3px",
        top: "-4mm",
        // paddingBottom: "6mm"
      }}
    >
      {/* Header */}
      <Box component="img" src={ShippingLanscape} width="100%" height="80px" />

      {/*detail*/}
      <Grid
        container
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: "100%",
          px: 2,
        }}
      >
        {/* Left empty to balance */}
        <Box sx={{ flex: 1 }} />

        {/* Center title */}
        <Typography
          sx={{
            fontFamily: FontFamily.openSans,
            fontSize: "14px !important",
            fontWeight: 700,
            flex: 2,
            textAlign: "center",
            marginLeft: "30px",
          }}
        >
          Shipping-Statement Of Account
        </Typography>

        {/* Generated date right aligned */}
        <Typography
          sx={{
            fontFamily: FontFamily.openSans,
            fontSize: "12px !important",
            fontWeight: 500,
            flex: 1,
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          Generated on: {moment(currentDate).format("DD-MM-YYYY")}
        </Typography>
      </Grid>

      <Box sx={{ p: 0.5, pr: 4.5 }}>

        <Grid container spacing={2} sx={{
          // background: "#ffffff",
          m: 0.5,
          color: 'black',
          // p: 2,
          // borderRadius: "12px",
          // boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          // border: "1px solid #ddd",
        }}>
          <Box className="invoice">
            <Box className="invoice-container">
              <Grid
                container
                spacing={1}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Grid item xs={7} sx={{ paddingLeft: "4px" }}>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        marginBottom: "5px",
                        fontFamily: FontFamily.openSans,
                        fontSize: "12px !important",
                        display: 'flex',
                      }}
                    >
                      Customer Information
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "start" }}>
                      <Typography

                        sx={{ display: "flex", fontSize: "10px !important", width: "80px", fontFamily: FontFamily.openSans }}>
                        Name:
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: "12px !important", fontFamily: FontFamily.openSans }}>
                        {selectedCustomer?.id ?? "-"} {"-"}   {selectedCustomer?.name ?? "-"}
                      </Typography>
                    </Box>
                    {/* <Box sx={{ display: "flex", alignItems: "start" }}>
                                            <Typography

                                                sx={{ display: "flex", fontSize: "12px !important", width: "80px", fontFamily: FontFamily.openSans }}>
                                                C-ID:
                                            </Typography>
                                            <Typography sx={{ fontSize: "12px !important", fontFamily: FontFamily.openSans }}>
                                                {selectedCustomer?.id ?? "-"}
                                            </Typography>
                                        </Box> */}
                    <Box sx={{ display: "flex", alignItems: "start" }}>
                      <Typography

                        sx={{ display: "flex", fontSize: "10px !important", width: "80px", fontFamily: FontFamily.openSans }}>
                        Period:
                      </Typography>
                      <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                        {moment(
                          fromdate
                        ).format("DD-MM-YYYY")} - {moment(
                          todate
                        ).format("DD-MM-YYYY")}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "start" }}>
                      <Typography

                        sx={{ display: "flex", fontSize: "10px !important", width: "80px", fontFamily: FontFamily.openSans }}>
                        Type:
                      </Typography>
                      <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                        {status?.name}
                      </Typography>
                    </Box>



                  </Box>
                </Grid>

                <Grid item xs={5}  >
                  <Box sx={{ display: "flex" }}>

                    <Box
                      sx={{
                        width: '20px',
                        height: '60px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginLeft: '10px',
                      }}
                    >
                      <Typography
                        sx={{
                          transform: 'rotate(-90deg)',
                          transformOrigin: 'center center',
                          fontWeight: 700,
                          fontFamily: FontFamily.openSans,
                          fontSize: "10px !important",
                          textAlign: 'center',
                          lineHeight: 1,
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        Ageing
                      </Typography>
                    </Box>

                    <Table sx={{ width: "95%", borderCollapse: "collapse", marginLeft: "10px", }}>
                      <TableBody>
                        <TableRow>
                          {/* Left Column */}
                          <TableCell sx={{ border: "1px solid green", background: Colors.primary, color: Colors.white, padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center"
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                Days
                              </Typography>
                            </Box>
                          </TableCell>
                          {/* Right Column */}
                          <TableCell sx={{ border: "1px solid green", background: Colors.primary, color: Colors.white, padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center"
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                Below 15
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{
                            border: "1px solid green", background: Colors.primary, color: Colors.white, padding: "2px",

                          }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                Btw 16-30
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid green", background: Colors.primary, color: Colors.white, padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center"
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                Over 30
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid green", background: Colors.primary, color: Colors.white, padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center"
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                Total
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          {/* Left Column */}
                          <TableCell sx={{ border: "1px solid green", background: Colors.primary, color: Colors.white, padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                Veh. Count
                              </Typography>

                            </Box>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid", padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                {parseFloat(dueBreakup?.below15DaysCount).toFixed(2)}

                              </Typography>

                            </Box>
                          </TableCell>

                          <TableCell sx={{ border: "1px solid ", padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                {parseFloat(dueBreakup?.below16To30DaysCount).toFixed(2)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid ", padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                {parseFloat(dueBreakup?.over30DaysCount).toFixed(2)}
                              </Typography>
                            </Box>
                          </TableCell><TableCell sx={{ border: "1px solid ", padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                {parseFloat(dueBreakup?.totalCount).toFixed(2)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          {/* Left Column */}
                          <TableCell sx={{ border: "1px solid green", background: Colors.primary, color: Colors.white, padding: "2px" }}>
                            <Box sx={{
                              extAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                Amount $
                              </Typography>

                            </Box>
                          </TableCell>

                          {/* Right Column */}
                          <TableCell sx={{ border: "1px solid", padding: "2px" }}>
                            <Box sx={{
                              extAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                {parseFloat(dueBreakup?.below15DaysTotal).toFixed(2)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid", padding: "2px" }}>
                            <Box sx={{
                              extAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                {parseFloat(dueBreakup?.below16To30DaysTotal).toFixed(2)}

                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ border: "1px solid ", padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                {parseFloat(dueBreakup?.over30DaysTotal).toFixed(2)}
                              </Typography>
                            </Box>
                          </TableCell><TableCell sx={{ border: "1px solid ", padding: "2px" }}>
                            <Box sx={{
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>

                              <Typography sx={{ fontSize: "10px !important", fontFamily: FontFamily.openSans }}>
                                {parseFloat(dueBreakup?.total).toFixed(2)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>

                      </TableBody>
                    </Table>
                  </Box>
                  {/* <Typography sx={{ fontSize: '16px', color: 'grey', display: 'flex', alignItems: "center", justifyContent: 'center', }}>As Per Galaxy Yard Date</Typography> */}
                </Grid>
              </Grid>
            </Box>

          </Box>


        </Grid>

      </Box>
      <Box sx={{
        p: 0.2, pr: 2.6,
        mb: 3,
      }}>

        {/* Table */}
        <TableContainer component={Paper} sx={{ marginBottom: "10px !important" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {visibleColumns.map((colIndex, i) => {
                  const headerText = tableHead[colIndex];

                  return (
                    <Cell key={colIndex}
                      style={{
                        // width:
                        //     colIndex === MAKE_MODEL_COL_INDEX && isShortHead
                        //     ? `${MAKE_MODEL_COL_WIDTH}px`
                        //     : `${BASE_COL_WIDTH}px`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          height: "100px",
                          paddingBottom: "45px",
                          paddingLeft: "5px",
                        }}
                      >
                        <Box
                          sx={{
                            transform: 'rotate(-90deg)',
                            transformOrigin: 'bottom',
                            fontSize: '12px !important',
                            fontWeight: 'bold',
                            fontFamily: '"Arial Black", Arial, sans-serif',
                            color: Colors.white,
                            width: "100px",
                            // width:  colIndex === MAKE_MODEL_COL_INDEX && isShortHead  ? `${MAKE_MODEL_COL_WIDTH}px` : `${BASE_COL_WIDTH}px`,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            textAlign: 'center',
                            marginTop: 'auto',
                            // border: '1px solid red',
                            marginBottom: '5px',
                          }}
                        >
                          {String(headerText).split('\n').map((line, i) => (
                            <div key={i}>{line}</div>
                          ))}
                        </Box>
                      </Box>
                    </Cell>
                  );
                })}
              </TableRow>

            </TableHead>
            <TableBody>



              {resultData?.length > 0 ? (
                <Fragment>
                  {resultData.map((item, index) => {

                    return (

                      <>

                        <TableRow key={index}
                          sx={{
                            background: (() => {
                              // On The Way condition
                              // if (
                              //   item?.vehicle?.arrived_port_date === null &&
                              //   item?.vehicle?.loading_date
                              // ) {
                              //   return "#d0e7ff"; // light blue
                              // }

                              // Arrived And Due condition
                              // const arrivedDate = moment(item?.vehicle?.arrived_port_date);
                              // const dueThresholdDate = moment(arrivedDate).add(15, "days");
                              // const today = moment();

                              // if (
                              //   today.isSameOrAfter(dueThresholdDate, "day") &&
                              //   item?.invoice?.balance !== "0.000" || item?.invoice?.balance !== "0.000"
                              // ) {
                              //   return "#ffd0d0"; // light red
                              // }

                              // Paid condition
                              // if (item?.invoice?.balance === "0.000") {
                              //   return "#d4fdd9"; // light green
                              // }

                              return index % 2 !== 0 ? "#EFF8E7" : "";
                            })(),
                          }}>
                          {visibleColumns.map((colIndex, idx) => {
                            const arrivedDate = moment(item?.vehicle?.arrived_port_date);
                            const dueThresholdDate = moment(arrivedDate).add(15, "days");
                            const today = moment();

                            const isOverdue =
                              today.isSameOrAfter(dueThresholdDate, "day") &&
                              item?.invoice?.balance !== "0.000";
                            const onTheWay =
                              item?.vehicle?.arrived_port_date === null && item?.vehicle?.loading_date;
                            const isPaid = item?.invoice?.balance === "0.000";

                            const lastIndex = visibleColumns.length - 1;
                            const secondLastIndex = visibleColumns.length - 2;
                            const thirdLastIndex = visibleColumns.length - 3;

                            const isTargetColumn =
                              idx === lastIndex || idx === secondLastIndex || idx === thirdLastIndex;

                            let bgColor = undefined;
                            if (isOverdue && isTargetColumn) {
                              bgColor = "#FFABAB";
                            } else if (isPaid && isTargetColumn) {
                              bgColor = "#4ab43e";
                            } else if (onTheWay && isTargetColumn) {
                              bgColor = "#6BC5ED";
                            }

                            return (
                              <Cell
                                key={colIndex}
                                sx={{
                                  fontSize: "10px !important",
                                  textAlign: "center",
                                  backgroundColor: bgColor,
                                  color: bgColor ? "#000000" : undefined,
                                  minWidth:
                                    colIndex === MAKE_MODEL_COL_INDEX && isShortHead
                                      ? MAKE_MODEL_COL_WIDTH
                                      : BASE_COL_WIDTH,
                                }}
                              >
                                {renderCellContent(item, colIndex, index)}
                              </Cell>
                            );
                          })}
                          {/* {visibleColumns.map((colIndex) => {
                            const arrivedDate = moment(item?.vehicle?.arrived_port_date);
                            const dueThresholdDate = moment(arrivedDate).add(15, "days");
                            const today = moment();

                            const isOverdue =
                              today.isSameOrAfter(dueThresholdDate, "day") &&
                              item?.invoice?.balance !== "0.000";

                            const onTheWay =
                              item?.vehicle?.arrived_port_date === null &&
                              item?.vehicle?.loading_date

                            const isPaid = item?.invoice?.balance === "0.000";
                            const isTargetColumn = colIndex === visibleColumns.length  || colIndex === visibleColumns.length - 1 || colIndex === visibleColumns.length - 2 || colIndex === visibleColumns.length - 3;
                            console.log(isTargetColumn, 'isTargetColumn')
                            console.log( visibleColumns.length ,  visibleColumns.length - 1,  visibleColumns.length - 2, visibleColumns.length - 3, colIndex, 'colIndex')
                            let bgColor = undefined;
                            if (isOverdue && isTargetColumn) {
                              bgColor = "#FFABAB";
                            } else if (isPaid && isTargetColumn) {
                              bgColor = "#4ab43e";
                            } else if (onTheWay && isTargetColumn) {
                              bgColor = "#6BC5ED";
                            }

                            return (
                              <Cell
                                key={colIndex}
                                sx={{
                                  fontSize: "10px !important",
                                  textAlign: "center",
                                  backgroundColor: bgColor,
                                  color: bgColor ? "#000000" : undefined, // black text for contrast
                                  minWidth: colIndex === MAKE_MODEL_COL_INDEX && isShortHead ? MAKE_MODEL_COL_WIDTH : BASE_COL_WIDTH,
                                }}
                              >
                                {renderCellContent(item, colIndex, index)}
                              </Cell>
                            );
                          })} */}

                          {/* {visibleColumns.map((colIndex) => {
                            const arrivedDate = moment(item?.vehicle?.arrived_port_date);
                            const dueThresholdDate = moment(arrivedDate).add(15, "days");
                            const today = moment();

                            const isOverdue =
                              today.isSameOrAfter(dueThresholdDate, "day") &&
                              item?.invoice?.balance !== "0.000";

                            const isTargetColumn = colIndex === 23 || colIndex === 26;

                            const isPaid = item?.invoice?.balance === "0.000";

                            return (
                              <Cell
                                key={colIndex}
                                sx={{
                                  fontSize: "8px",
                                  textAlign: "center",
                                  backgroundColor: isOverdue && isTargetColumn ? "#b22222" : undefined,
                                }}
                              >
                                {renderCellContent(item, colIndex, index)}
                              </Cell>
                            );
                          })} */}

                        </TableRow>
                      </>
                    )
                  })}
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 7} align="right">
                      <Typography
                        className="pdf-table"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "12px !important", fontWeight: 700 }}
                      >

                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "12px !important", fontWeight: 700, color: 'blue' }}
                      >

                        {'SubTotal'}
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>

                    <TableCell>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "12px !important", fontWeight: 700, color: "#FFBF00", lineHeight: '0 !important', whiteSpace: 'normal', width: '100px', marginLeft: '5px' }}
                      >

                        {'Net Due'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "14px !important", fontWeight: 700, color: "green" }}
                      >
                        {'Paid'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "14px !important", fontWeight: 700, color: 'red' }}
                      >

                        {'Balance Due'}
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>

                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 7} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700, color: 'blue' }}
                      >
                        $
                        {CommaSeparator(
                          parseFloat(subTotal).toFixed(2)
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>

                    <TableCell>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700, color: "#FFBF00" }}
                      >
                        $
                        {CommaSeparator(
                          parseFloat(mainTotal).toFixed(2)
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700, color: "green" }}
                      >
                        $
                        {CommaSeparator(
                          parseFloat(totalPaid).toFixed(2)
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700, color: 'red' }}
                      >
                        $
                        {CommaSeparator(
                          parseFloat(totalBalance).toFixed(2)
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>

                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 4} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}

                      >
                        Total Parking Due (for Breakdown Download Gate Pass Report)
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>

                    <TableCell colSpan={3}>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                        ${formatAccounting((parseFloat(totalParkingWallet)).toFixed(2))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 4} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}

                      >

                        Total Shipping Due (including Parking)

                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>

                    <TableCell colSpan={3}>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                        $
                        {formatAccounting(
                          parseFloat(
                            parseFloat(totalBalance) +
                            parseFloat(totalParkingWallet)
                          ).toFixed(2)
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 4} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}

                      >
                        Shipping Wallet Balance

                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>


                    <TableCell colSpan={3}>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700,color: totalShippingWallet < 0 ? "red" : "green", }}
                      >
                        ${formatAccounting((parseFloat(totalShippingWallet)).toFixed(2))}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 4} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                        Due After Shipping Wallet/Ledger Balance
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>


                    <TableCell colSpan={3}>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                        ${" "}
                        {formatAccounting(
                          parseFloat(netDueTotal).toFixed(2)
                        )}
                      </Typography>
                    </TableCell>

                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 4} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}

                      >
                        Vehicle Wallet Balance
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>


                    <TableCell colSpan={3}>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 ,color: totalVehicleWallet < 0 ? "red" : "green",}}
                      >
                        $ {formatAccounting(
                          parseFloat(((totalVehicleWallet && currencyExchangeRate)
                            ? (totalVehicleWallet / currencyExchangeRate)
                            : 0
                          ) * -1).toFixed(2)
                        )}

                      </Typography>
                    </TableCell>

                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 4} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}

                      >
                        Net Due (After Shipping And Vehicle Wallet Balance)
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>

                    <TableCell colSpan={3}>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                        ${formatAccounting(
                          parseFloat(
                            parseFloat(netDueTotal) +
                            parseFloat((
                              (totalVehicleWallet && currencyExchangeRate)
                                ? (totalVehicleWallet / currencyExchangeRate)
                                : 0
                            ) * -1)
                          ).toFixed(2)
                        )}

                      </Typography>
                    </TableCell>

                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 4} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}

                      >
                        Undue Custom (Not Charged Yet)
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>

                    <TableCell colSpan={3}>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                        ${formatAccounting(parseFloat(totalCustomWallet).toFixed(2))}

                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={visibleColumns.length - 4} align="right">
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                        Shipping Recieveable with Undue Custom
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell colSpan={3}>
                      <Typography
                        className="pdf-table3"
                        variant="body1"
                        sx={{ fontFamily: FontFamily.openSans, fontSize: "16px !important", fontWeight: 700 }}
                      >
                        ${formatAccounting(
                          parseFloat(parseFloat(netDueTotal) + parseFloat(((totalVehicleWallet && currencyExchangeRate)
                            ? (totalVehicleWallet / currencyExchangeRate)
                            : 0
                          ) * -1) + parseFloat(totalCustomWallet)).toFixed(2)
                        )}

                      </Typography>
                    </TableCell>

                  </TableRow>
                </Fragment>
              ) : (
                <TableRow>
                  <TableCell colSpan={tableHead.length + 1} align="center" sx={{ fontWeight: 600 }}>
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

        </TableContainer>

      </Box>
      {/* Footer */}

      <Box
        className="bottom-box"
        sx={{
          marginTop: "8px",
          // position: "absolute",
          bottom: 1,
          left: 1,
          right: 0,
          top: 1,
        }}
      >
        <Box
          sx={{
            background: "#EFF8E7",
            color: "black",
            display: "flex",
            justifyContent: "start",
            minWidth: "100%",
          }}
        >
          <Box
            sx={{
              width: " 100%",
              padding: "2px 6px",
              textAlign: "start",
            }}
          >
            <Typography
              sx={{
                fontSize: "8px !important",
                fontWeight: 700,
                display: "flex",
                justifyContent: "start",
                fontFamily: FontFamily.openSans,
              }}
            >
              1- This is a system-generated statement and does not require a
              signature. Any discrepancies in this SOA should be reported within
              2 days.
            </Typography>
            <Typography
              sx={{
                fontSize: "8px !important",
                fontWeight: 700,
                display: "flex",
                justifyContent: "start",
                fontFamily: FontFamily.openSans,
              }}
            >
              2- Relist payments should be made on the next day of the relist.
              Otherwise, your account will be Locked on the second day and a 15%
              penalty will be charged on relists.
            </Typography>
            <Typography
              sx={{
                fontSize: "8px !important",
                fontWeight: 700,
                display: "flex",
                justifyContent: "start",
                fontFamily: FontFamily.openSans,
              }}
            >
              3- Kindly settle the payment due at the earliest, we would like to
              remind you to be in touch with our customer service support team.
            </Typography>
            <Typography
              sx={{
                fontSize: "8px !important",
                fontWeight: 700,
                display: "flex",
                justifyContent: "start",
                fontFamily: FontFamily.openSans,
              }}
            >
              4-GALAXY SHALL NOT BE RESPONSIBLE FOR YOUR CONTAINERS STORAGE AND DETENTION IN PORTS
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: " 100%",
            color: "#FFFFFF",
            padding: "1%",
            backgroundColor: " #5abc0e",
            textAlign: "start",
            fontSize: "8px !important",
            fontWeight: 700,
          }}
        >
          <Typography
            sx={{
              wordBreak: "break-word",
              fontSize: "8px !important",
              fontWeight: 700,
              fontFamily: FontFamily.openSans,
            }}
            className="footer-div-font"
          >
            Customer care Contact: Mohammed husni - +971523195682 (Arabic &
            English ) Ardamehr Shoev - +971545836028 (English ,Arabic, Tajik &
            Farsi) Ravin abdul kareem - +971528293801 (Kurdish , Arabic &
            English) Magsat Gylyjov - +97158666403 (Turken , Russian & English)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ShippingSoaPdf;
